import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api, API, useAuth } from '../App';
import { Eye, X, ChevronLeft, ChevronRight, MapPin, Building2, User, CheckSquare, Pencil, Save } from 'lucide-react';

export default function ChecklistKayitlar() {
  const { user } = useAuth();
  const [kayitlar, setKayitlar] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [personeller, setPersoneller] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detay, setDetay] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editCevaplar, setEditCevaplar] = useState({});
  const [editNotlar, setEditNotlar] = useState('');
  const [saving, setSaving] = useState(false);
  const [sayfa, setSayfa] = useState(1);
  const limit = 20;

  const [filtre, setFiltre] = useState({ lokasyon_id: '', personel_id: '', baslangic: '', bitis: '' });

  const load = async (page = sayfa) => {
    setLoading(true);
    try {
      const { data } = await api.get('/checklistler/kontroller/liste', { params: { sayfa: page, limit, ...filtre } });
      setKayitlar(data.kontroller);
      setToplam(data.toplam);
    } catch { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get('/lokasyonlar').then(r => setLokasyonlar(r.data)).catch(() => {});
    if (user.rol !== 'personel') {
      api.get('/kullanicilar/personeller/liste').then(r => setPersoneller(r.data)).catch(() => {});
    }
  }, []);

  useEffect(() => { load(sayfa); }, [sayfa]);

  const handleSearch = e => {
    e.preventDefault(); setSayfa(1); load(1);
  };

  const openDetay = async (id) => {
    try {
      const { data } = await api.get(`/checklistler/kontroller/${id}`);
      setDetay(data);
      setEditCevaplar({});
      setEditNotlar(data.kontrol?.notlar || '');
      setEditMode(false);
    } catch { toast.error('Detay yüklenemedi'); }
  };

  const startEdit = () => {
    const init = {};
    detay.cevaplar.forEach(c => {
      init[c.soru_id] = { soru_id: c.soru_id, evet_hayir: c.evet_hayir, puan: c.puan, not_metni: c.not_metni || '' };
    });
    setEditCevaplar(init);
    setEditMode(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('notlar', editNotlar);
      fd.append('cevaplar', JSON.stringify(Object.values(editCevaplar)));
      await api.put(`/checklistler/kontroller/${detay.kontrol.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Güncellendi');
      setEditMode(false);
      openDetay(detay.kontrol.id);
      load(sayfa);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata');
    } finally { setSaving(false); }
  };

  const setCevap = (id, field, val) => {
    setEditCevaplar(prev => ({ ...prev, [id]: { ...prev[id], soru_id: id, [field]: val } }));
  };

  const sayfaSayisi = Math.ceil(toplam / limit);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Checklist Kayıtları</h1>
          <p className="page-subtitle">Toplam {toplam} kontrol kaydı</p>
        </div>
      </div>

      {/* Filtreler */}
      <form onSubmit={handleSearch} className="card filter-card" style={{ padding: 16, marginBottom: 20 }}>
        <div className="input-group input-group-wide">
          <label className="input-label">Lokasyon</label>
          <select className="input" value={filtre.lokasyon_id} onChange={e => setFiltre({ ...filtre, lokasyon_id: e.target.value })}>
            <option value="">Tümü</option>
            {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
          </select>
        </div>
        {user.rol !== 'personel' && (
          <div className="input-group">
            <label className="input-label">Personel</label>
            <select className="input" value={filtre.personel_id} onChange={e => setFiltre({ ...filtre, personel_id: e.target.value })}>
              <option value="">Tümü</option>
              {personeller.map(p => <option key={p.id} value={p.id}>{p.ad} {p.soyad}</option>)}
            </select>
          </div>
        )}
        <div className="input-group">
          <label className="input-label">Başlangıç</label>
          <input className="input" type="date" value={filtre.baslangic} onChange={e => setFiltre({ ...filtre, baslangic: e.target.value })} />
        </div>
        <div className="input-group">
          <label className="input-label">Bitiş</label>
          <input className="input" type="date" value={filtre.bitis} onChange={e => setFiltre({ ...filtre, bitis: e.target.value })} />
        </div>
        <button type="submit" className="btn btn-primary">Filtrele</button>
        <button type="button" className="btn btn-secondary" onClick={() => { setFiltre({ lokasyon_id: '', personel_id: '', baslangic: '', bitis: '' }); setSayfa(1); setTimeout(() => load(1), 0); }}>Temizle</button>
      </form>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-blue" style={{ width: 32, height: 32 }} />
        </div>
      ) : (
        <>
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Lokasyon</th>
                    <th>Bina</th>
                    <th>Personel</th>
                    <th>Checklist</th>
                    <th>Tarih</th>
                    <th>Durum</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {kayitlar.map(k => (
                    <tr key={k.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <MapPin size={13} color="var(--blue)" />{k.lokasyon_adi}
                        </div>
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--gray-600)' }}>{k.bina_adi || '—'}</td>
                      <td style={{ fontSize: 13 }}>{k.personel_adi || '—'}</td>
                      <td style={{ fontSize: 13, color: 'var(--gray-600)' }}>{k.checklist_baslik || '—'}</td>
                      <td style={{ fontSize: 13, color: 'var(--gray-600)' }}>
                        {new Date(k.kontrol_saati).toLocaleString('tr-TR')}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {k.benim && <span className="badge badge-blue">Benim</span>}
                          {k.bugun && <span className="badge badge-green">Bugün</span>}
                        </div>
                      </td>
                      <td>
                        <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openDetay(k.id)}>
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!kayitlar.length && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>Kayıt bulunamadı</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {sayfaSayisi > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 20 }}>
              <button className="btn btn-secondary btn-sm" disabled={sayfa <= 1} onClick={() => setSayfa(s => s - 1)}>
                <ChevronLeft size={15} /> Önceki
              </button>
              <span style={{ fontSize: 14, color: 'var(--gray-600)' }}>{sayfa} / {sayfaSayisi}</span>
              <button className="btn btn-secondary btn-sm" disabled={sayfa >= sayfaSayisi} onClick={() => setSayfa(s => s + 1)}>
                Sonraki <ChevronRight size={15} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Detay Modal */}
      {detay && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDetay(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div>
                <span className="modal-title">{detay.kontrol?.checklist_baslik || 'Checklist Detayı'}</span>
                <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>
                  {detay.kontrol?.lokasyon_adi} · {new Date(detay.kontrol?.kontrol_saati).toLocaleString('tr-TR')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {detay.kontrol?.benim && detay.kontrol?.bugun && !editMode && (
                  <button className="btn btn-secondary btn-sm" onClick={startEdit}>
                    <Pencil size={13} /> Düzenle
                  </button>
                )}
                {editMode && (
                  <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                    {saving ? <div className="spinner" style={{ width: 13, height: 13 }} /> : <Save size={13} />} Kaydet
                  </button>
                )}
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setDetay(null)}><X size={16} /></button>
              </div>
            </div>
            <div className="modal-body">
              {/* Özet */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                  { label: 'Personel', val: detay.kontrol?.personel_adi || '—' },
                  { label: 'Lokasyon', val: detay.kontrol?.lokasyon_adi },
                  { label: 'Bina', val: detay.kontrol?.bina_adi || '—' },
                  { label: 'Tarih', val: new Date(detay.kontrol?.kontrol_saati).toLocaleString('tr-TR') },
                ].map(({ label, val }) => (
                  <div key={label} style={{ padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 10, border: '1px solid var(--gray-100)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Notlar */}
              {editMode ? (
                <div style={{ marginBottom: 14 }}>
                  <label className="input-label">Notlar</label>
                  <textarea className="input" rows={2} value={editNotlar} onChange={e => setEditNotlar(e.target.value)} style={{ resize: 'vertical' }} />
                </div>
              ) : detay.kontrol?.notlar ? (
                <div style={{ padding: '10px 14px', background: '#fffbeb', borderRadius: 10, border: '1px solid #fef08a', marginBottom: 14, fontSize: 13 }}>
                  <strong>Notlar:</strong> {detay.kontrol.notlar}
                </div>
              ) : null}

              {/* Cevaplar */}
              <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Cevaplar</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {detay.cevaplar?.map(c => {
                  const ec = editCevaplar[c.soru_id] || {};
                  return (
                    <div key={c.id} style={{ padding: '12px 14px', background: 'var(--gray-50)', borderRadius: 10, border: '1px solid var(--gray-100)' }}>
                      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>{c.soru_metni}</div>
                      {editMode ? (
                        c.soru_tipi === 'evet_hayir' ? (
                          <div style={{ display: 'flex', gap: 8 }}>
                            {[{ val: true, label: '✓ Evet' }, { val: false, label: '✗ Hayır' }].map(opt => (
                              <button key={String(opt.val)} type="button" onClick={() => setCevap(c.soru_id, 'evet_hayir', opt.val)}
                                style={{ flex: 1, padding: '8px', borderRadius: 8, border: `2px solid ${ec.evet_hayir === opt.val ? (opt.val ? '#16a34a' : '#dc2626') : 'var(--gray-200)'}`, background: ec.evet_hayir === opt.val ? (opt.val ? '#f0fdf4' : '#fef2f2') : '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 6 }}>
                            {[1,2,3,4,5].map(p => (
                              <button key={p} type="button" onClick={() => setCevap(c.soru_id, 'puan', p)}
                                style={{ width: 38, height: 38, borderRadius: 8, border: `2px solid ${ec.puan === p ? 'var(--blue)' : 'var(--gray-200)'}`, background: ec.puan === p ? '#eff6ff' : '#fff', cursor: 'pointer', fontWeight: 700 }}>
                                {p}
                              </button>
                            ))}
                          </div>
                        )
                      ) : (
                        <>
                          {c.evet_hayir !== null && c.evet_hayir !== undefined && (
                            <span className={`badge ${c.evet_hayir ? 'badge-green' : 'badge-red'}`}>{c.evet_hayir ? '✓ Evet' : '✗ Hayır'}</span>
                          )}
                          {c.puan && <span className="badge badge-blue">Puan: {c.puan}/5</span>}
                          {c.not_metni && <p style={{ fontSize: 12, color: 'var(--gray-600)', margin: '6px 0 0' }}>{c.not_metni}</p>}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Fotoğraflar */}
              {detay.fotograflar?.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Fotoğraflar</h4>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {detay.fotograflar.map(f => (
                      <a key={f.id} href={`${API.replace('/api','')}/uploads/checklist/${f.dosya_yolu}`} target="_blank" rel="noreferrer">
                        <img src={`${API.replace('/api','')}/uploads/checklist/${f.dosya_yolu}`} alt="" style={{ width: 80, height: 80, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--gray-100)' }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
